import { useState, useMemo } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  TextField,
  Button,
  Divider,
  Alert,
} from '@mui/material';
import { CalendarToday, Save } from '@mui/icons-material';
import { InvestmentChecklistDTO, ChecklistDueAnchor } from '@inversor-hub/types';
import { useUpdateChecklistAnchors } from '@/lib/api/investment-checklist';
import { useSnackbar } from 'notistack';
import { ANCHOR_LABELS } from './checklist-constants';
import dayjs from 'dayjs';

interface ChecklistAnchorsPanelProps {
  checklist: InvestmentChecklistDTO;
}

const ChecklistAnchorsPanel = ({ checklist }: ChecklistAnchorsPanelProps) => {
  const [signingDate, setSigningDate] = useState(
    checklist.signingDate ? dayjs(checklist.signingDate).format('YYYY-MM-DD') : ''
  );
  const [closingDate, setClosingDate] = useState(
    checklist.closingDate ? dayjs(checklist.closingDate).format('YYYY-MM-DD') : ''
  );
  const [moveInDate, setMoveInDate] = useState(
    checklist.moveInDate ? dayjs(checklist.moveInDate).format('YYYY-MM-DD') : ''
  );
  const [feinDate, setFeinDate] = useState(
    checklist.feinDate ? dayjs(checklist.feinDate).format('YYYY-MM-DD') : ''
  );
  const [arrasExpiryDate, setArrasExpiryDate] = useState(
    checklist.arrasExpiryDate ? dayjs(checklist.arrasExpiryDate).format('YYYY-MM-DD') : ''
  );

  const { mutate: updateAnchors, isPending } = useUpdateChecklistAnchors(checklist.investmentId);
  const { enqueueSnackbar } = useSnackbar();

  const anchorUsageCounts = useMemo(() => {
    const counts: Record<ChecklistDueAnchor, number> = {
      SIGNING_DATE: 0,
      CLOSING_DATE: 0,
      MOVE_IN_DATE: 0,
      FEIN_DATE: 0,
      ARRAS_EXPIRY_DATE: 0,
    };

    checklist.tasks.forEach((task) => {
      if (task.dueAnchor && task.status === 'PENDING') {
        counts[task.dueAnchor]++;
      }
    });

    return counts;
  }, [checklist.tasks]);

  const hasChanges = () => {
    const current = {
      signingDate: checklist.signingDate ? dayjs(checklist.signingDate).format('YYYY-MM-DD') : '',
      closingDate: checklist.closingDate ? dayjs(checklist.closingDate).format('YYYY-MM-DD') : '',
      moveInDate: checklist.moveInDate ? dayjs(checklist.moveInDate).format('YYYY-MM-DD') : '',
      feinDate: checklist.feinDate ? dayjs(checklist.feinDate).format('YYYY-MM-DD') : '',
      arrasExpiryDate: checklist.arrasExpiryDate
        ? dayjs(checklist.arrasExpiryDate).format('YYYY-MM-DD')
        : '',
    };

    return (
      signingDate !== current.signingDate ||
      closingDate !== current.closingDate ||
      moveInDate !== current.moveInDate ||
      feinDate !== current.feinDate ||
      arrasExpiryDate !== current.arrasExpiryDate
    );
  };

  const closingDateValidation = useMemo(() => {
    if (!closingDate) return { error: false, helperText: '' };

    const closing = dayjs(closingDate);

    // Cannot be greater than arrasExpiryDate
    if (arrasExpiryDate && closing.isAfter(dayjs(arrasExpiryDate))) {
      return {
        error: true,
        helperText:
          'La fecha de compraventa no puede ser posterior a la fecha de expiración de arras',
      };
    }

    // Cannot be lower than 10 days after feinDate
    if (feinDate) {
      const minClosingDate = dayjs(feinDate).add(10, 'day');
      if (closing.isBefore(minClosingDate)) {
        return {
          error: true,
          helperText: 'La fecha de compraventa debe ser al menos 10 días después de la fecha FEIN',
        };
      }
    }

    return { error: false, helperText: '' };
  }, [closingDate, arrasExpiryDate, feinDate]);

  const hasValidationErrors = closingDateValidation.error;

  const handleSave = () => {
    updateAnchors(
      {
        signingDate: signingDate || null,
        closingDate: closingDate || null,
        moveInDate: moveInDate || null,
        feinDate: feinDate || null,
        arrasExpiryDate: arrasExpiryDate || null,
      },
      {
        onSuccess: () => {
          enqueueSnackbar('Fechas actualizadas correctamente', { variant: 'success' });
        },
        onError: () => {
          enqueueSnackbar('Error al actualizar las fechas', { variant: 'error' });
        },
      }
    );
  };

  return (
    <Card sx={{ position: 'sticky', top: 16, boxShadow: 3 }}>
      <CardContent>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <CalendarToday color="primary" />
          <Typography variant="h6" fontWeight={600}>
            Fechas clave
          </Typography>
        </Box>

        <Alert severity="info" sx={{ mb: 2 }}>
          Estas fechas se utilizan para calcular automáticamente las fechas de vencimiento de las
          tareas.
        </Alert>

        <Divider sx={{ my: 2 }} />

        <Box display="flex" flexDirection="column" gap={2}>
          <Box>
            <Typography variant="body2" color="text.secondary" mb={0.5}>
              {ANCHOR_LABELS.SIGNING_DATE}
            </Typography>
            <TextField
              type="date"
              size="small"
              fullWidth
              value={signingDate}
              onChange={(e) => setSigningDate(e.target.value)}
              disabled={isPending}
              InputLabelProps={{ shrink: true }}
            />
            {anchorUsageCounts.SIGNING_DATE > 0 && (
              <Typography variant="caption" color="text.secondary" mt={0.5}>
                Usada por {anchorUsageCounts.SIGNING_DATE}{' '}
                {anchorUsageCounts.SIGNING_DATE === 1 ? 'tarea' : 'tareas'}
              </Typography>
            )}
          </Box>

          <Box>
            <Typography variant="body2" color="text.secondary" mb={0.5}>
              {ANCHOR_LABELS.ARRAS_EXPIRY_DATE}
            </Typography>
            <TextField
              type="date"
              size="small"
              fullWidth
              value={arrasExpiryDate}
              onChange={(e) => setArrasExpiryDate(e.target.value)}
              disabled={isPending}
              InputLabelProps={{ shrink: true }}
            />
            {anchorUsageCounts.ARRAS_EXPIRY_DATE > 0 && (
              <Typography variant="caption" color="text.secondary" mt={0.5}>
                Usada por {anchorUsageCounts.ARRAS_EXPIRY_DATE}{' '}
                {anchorUsageCounts.ARRAS_EXPIRY_DATE === 1 ? 'tarea' : 'tareas'}
              </Typography>
            )}
          </Box>

          <Box>
            <Typography variant="body2" color="text.secondary" mb={0.5}>
              {ANCHOR_LABELS.FEIN_DATE}
            </Typography>
            <TextField
              type="date"
              size="small"
              fullWidth
              value={feinDate}
              onChange={(e) => setFeinDate(e.target.value)}
              disabled={isPending}
              InputLabelProps={{ shrink: true }}
            />
            {anchorUsageCounts.FEIN_DATE > 0 && (
              <Typography variant="caption" color="text.secondary" mt={0.5}>
                Usada por {anchorUsageCounts.FEIN_DATE}{' '}
                {anchorUsageCounts.FEIN_DATE === 1 ? 'tarea' : 'tareas'}
              </Typography>
            )}
          </Box>

          <Box>
            <Typography variant="body2" color="text.secondary" mb={0.5}>
              {ANCHOR_LABELS.CLOSING_DATE}
            </Typography>
            <TextField
              type="date"
              size="small"
              fullWidth
              value={closingDate}
              onChange={(e) => setClosingDate(e.target.value)}
              disabled={isPending}
              InputLabelProps={{ shrink: true }}
              error={closingDateValidation.error}
              helperText={closingDateValidation.helperText}
            />
            {anchorUsageCounts.CLOSING_DATE > 0 && !closingDateValidation.error && (
              <Typography variant="caption" color="text.secondary" mt={0.5}>
                Usada por {anchorUsageCounts.CLOSING_DATE}{' '}
                {anchorUsageCounts.CLOSING_DATE === 1 ? 'tarea' : 'tareas'}
              </Typography>
            )}
          </Box>

          <Box>
            <Typography variant="body2" color="text.secondary" mb={0.5}>
              {ANCHOR_LABELS.MOVE_IN_DATE}
            </Typography>
            <TextField
              type="date"
              size="small"
              fullWidth
              value={moveInDate}
              onChange={(e) => setMoveInDate(e.target.value)}
              disabled={isPending}
              InputLabelProps={{ shrink: true }}
            />
            {anchorUsageCounts.MOVE_IN_DATE > 0 && (
              <Typography variant="caption" color="text.secondary" mt={0.5}>
                Usada por {anchorUsageCounts.MOVE_IN_DATE}{' '}
                {anchorUsageCounts.MOVE_IN_DATE === 1 ? 'tarea' : 'tareas'}
              </Typography>
            )}
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Button
          fullWidth
          variant="contained"
          startIcon={<Save />}
          onClick={handleSave}
          disabled={!hasChanges() || isPending || hasValidationErrors}
        >
          Guardar fechas
        </Button>
      </CardContent>
    </Card>
  );
};

export default ChecklistAnchorsPanel;
